import React, { useState, useEffect, useContext } from "react";
import { serverHost } from "variables/serverdata";
import { useNavigate, useParams } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import HusariaHeader from "views/pages/components/HusariaHeader";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Alert,
} from "react-bootstrap";

const BridgesAddEdit = ({ method }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState([]);
  const [error, setError] = useState("");
  const [gatewayList, setGatewayList] = useState([]);
  const [formData, setFormData] = useState({
    bridge_name: "",
    bridge_action: "",
    bridge_profile: "",
    bridge_gateway_1: "",
    bridge_gateway_2: "",
    bridge_gateway_3: "",
    bridge_destination: "",
    bridge_enabled: true,
    bridge_description: "",
  });

  const actionOptions = [
    { value: "", label: "Select..." },
    { value: "user", label: "User" },
    { value: "gateway", label: "Gateway" },
    { value: "profile", label: "Profile" },
    { value: "loopback", label: "Loopback" },
  ];

  const profileOptions = [
    { value: "", label: "Select..." },
    { value: "external", label: "external" },
    { value: "external-ipv6", label: "external-ipv6" },
    { value: "internal", label: "internal" },
    { value: "internal-ipv6", label: "internal-ipv6" },
  ];

  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const res = await fetch(
          `${serverHost}/get_rows?table=v_gateways&pageSize=50&page=1`,
        );
        if (!res.ok) return;
        const data = await res.json();
        const mapped = data.results.map((g) => ({
          gateway_uuid: g.gateway_uuid,
          name: g.gateway,
        }));
        setGatewayList(mapped);
      } catch (err) {
        console.error("Failed to fetch gateways:", err);
      }
    };

    const fetchBridge = async () => {
      try {
        const res = await fetch(
          `${serverHost}/fetch_bridge/${encodeURIComponent(id)}`,
        );
        if (!res.ok)
          throw new Error(
            res.status === 404 ? "Row not found" : "Failed to fetch row",
          );
        const data = await res.json();
        if (!data?.result) throw new Error("Row not found");
        const bridge = data.result;
        setFormData({
          ...bridge,
          bridge_enabled: bridge.bridge_enabled === "true",
        });
      } catch (err) {
        console.log("Failed to fetch bridge:", err);
        setError("Failed to fetch Bridge: " + err.message);
      }
    };
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchGateways(), id ? fetchBridge() : undefined]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [method, id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Clear dependent fields when action changes
    if (name === "bridge_action") {
      setFormData((prev) => ({
        ...prev,
        bridge_profile: "",
        bridge_gateway_1: "",
        bridge_gateway_2: "",
        bridge_gateway_3: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !formData.bridge_name ||
      !formData.bridge_action ||
      !formData.bridge_destination
    ) {
      setError("Please fill in all required fields");
      return;
    }

    if (formData.bridge_action === "gateway") {
      if (
        !formData.bridge_gateway_1 &&
        !formData.bridge_gateway_2 &&
        !formData.bridge_gateway_3
      ) {
        setError("Please select at least one gateway");
        return;
      }
    }

    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const payload = {
        data: {
          ...formData,
        },
        client_uuid: site.client_uuid,
        site_name: site.company_name,
        insert_uuid: JSON.parse(localStorage.getItem("user")).user_uuid,
        method: method,
      };

      const targetId = method === "edit" ? id : site.site_uuid;
      const url = `${serverHost}/bridge/${encodeURIComponent(targetId)}`;

      if (method === "edit") {
        payload.site_uuid = site.site_uuid;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate("/admin/hucs/bridges");
      } else {
        const error = await response.json();
        console.error("Failed to save bridge:", error);
        setError("Failed to save bridge: " + error.message);
      }
    } catch (err) {
      setError("Failed to save Bridge: " + err.message);
    }
  };

  const handleCancel = () => {
    navigate("/admin/hucs/bridges");
  };

  const inputStyle = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    fontSize: "1rem",
    lineHeight: "1.5",
    borderRadius: "0.25rem",
    border: "1px solid",
    backgroundColor: isDark ? "#374151" : "#fff",
    color: isDark ? "#e2e8f0" : "#495057",
    borderColor: isDark ? "#4b5563" : "#ced4da",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "0.5rem",
    color: isDark ? "#e2e8f0" : "#495057",
    fontWeight: 500,
  };

  if (loading) {
    return (
      <>
        <HusariaHeader
          name="Edit Inbound Route"
          parentName="HUCS"
          padding="4.5rem"
        />
        <Container className="mt--6">
          <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
            <Card.Body className="p-4 text-center">
              <p>
                Loading Bridge<span className="loading-dots"/>
              </p>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <HusariaHeader
        name={`${method.toUpperCase()} Bridge`}
        parentName="HUCS"
        padding="4.5rem"
      />
      <Container fluid className="mt--6">
        <Card className="shadow-sm" style={{ border: "none" }}>
          <div
            style={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              borderRadius: "0.5rem",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <Card.Header
              style={{
                backgroundColor: isDark ? "#1f2937" : "#e9ecef",
                color: isDark ? "#e2e8f0" : "#1e3a5f",
                borderBottom: "none",
                padding: "1rem 1.5rem",
              }}
            >
              <h5 className="mb-0" style={{ fontWeight: 600 }}>
                {method.toUpperCase()} BRIDGE
              </h5>
            </Card.Header>

            <Card.Body
              style={{
                backgroundColor: isDark ? "#1f2937" : "#fff",
                padding: "1.5rem",
              }}
            >
              {error && (
                <div
                  style={{
                    backgroundColor: "#f8d7da",
                    color: "#721c24",
                    padding: "0.75rem 1.25rem",
                    marginBottom: "1rem",
                    borderRadius: "0.25rem",
                    border: "1px solid #f5c6cb",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  ! {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Name and Action */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div>
                    <label title="Required" style={{ ...labelStyle, fontWeight: "bolder" }}>
                      Name <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="bridge_name"
                      value={formData.bridge_name}
                      onChange={handleChange}
                      placeholder="Enter bridge name"
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={{ ...labelStyle, fontWeight: "bolder" }}>
                      Action <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <select
                      name="bridge_action"
                      value={formData.bridge_action}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    >
                      {actionOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Conditional Profile Field */}
                {formData.bridge_action === "profile" && (
                  <div style={{ marginBottom: "1rem" }}>
                    <div style={{ maxWidth: "50%" }}>
                      <label style={labelStyle}>Profile</label>
                      <select
                        name="bridge_profile"
                        value={formData.bridge_profile}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        {profileOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Conditional Gateway Fields */}
                {formData.bridge_action === "gateway" && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <div>
                      <label style={{ ...labelStyle, fontWeight: "bolder" }}>
                        Gateway 1 <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <select
                        name="bridge_gateway_1"
                        value={formData.bridge_gateway_1}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        <option value="">Select...</option>
                        {gatewayList.map((gw) => (
                          <option key={gw.gateway_uuid} value={gw.gateway_uuid}>
                            {gw.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={labelStyle}>Gateway 2</label>
                      <select
                        name="bridge_gateway_2"
                        value={formData.bridge_gateway_2}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        <option value="">Select...</option>
                        {gatewayList.map((gw) => (
                          <option key={gw.gateway_uuid} value={gw.gateway_uuid}>
                            {gw.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={labelStyle}>Gateway 3</label>
                      <select
                        name="bridge_gateway_3"
                        value={formData.bridge_gateway_3}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        <option value="">Select...</option>
                        {gatewayList.map((gw) => (
                          <option key={gw.gateway_uuid} value={gw.gateway_uuid}>
                            {gw.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Destination Number */}
                <div style={{ marginBottom: "1rem" }}>
                  <div style={{ maxWidth: "50%" }}>
                    <label style={{ ...labelStyle, fontWeight: "bolder" }}>
                      Destination Number{" "}
                      <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="bridge_destination"
                      value={formData.bridge_destination}
                      onChange={handleChange}
                      placeholder="Enter destination number"
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Description */}
                <div style={{ marginBottom: "1rem" }}>
                  <label style={labelStyle}>Description</label>
                  <textarea
                    name="bridge_description"
                    value={formData.bridge_description}
                    onChange={handleChange}
                    placeholder="Enter description"
                    rows={3}
                    style={{ ...inputStyle, resize: "vertical" }}
                  />
                </div>

                {/* Enabled Toggle */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                    Enabled
                  </span>

                  <label className="custom-toggle mb-0">
                    <input
                      type="checkbox"
                      id="bridge_enabled"
                      name="bridge_enabled"
                      checked={formData.bridge_enabled}
                      onChange={handleChange}
                    />
                    <span
                      className="custom-toggle-slider rounded-circle"
                      data-label-off="Off"
                      data-label-on="On"
                    />
                  </label>
                </div>

                {/* Action Buttons */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "2rem",
                  }}
                >
                  <Button variant="danger" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button variant="success" type="submit">
                    {method.toUpperCase()} Bridge
                  </Button>
                </div>
              </form>
            </Card.Body>
          </div>
        </Card>
      </Container>
    </>
  );
};

export default BridgesAddEdit;
